// Static configuration for the New Company Wizard V3.
// Plans, add-ons, durations, payment methods, color tokens, and the
// role-bundle mapping each plan grants on the backend.

export const COLORS = {
  navy: '#0f1847',
  navyDeep: '#070b24',
  navyDark: '#0d1340',
  navyMid: '#111b5e',
  gold: '#D4AF37',
  goldDark: '#B8960C',
  blue: '#1a3a8f',
  purple: '#8B6FC0',
  green: '#22a06b',
  border: '#e2e6f0',
  inputBg: '#fafbfc',
};

export const COMP_TYPE = {
  GLOBAL: 'global',
  ALPHA: 'alpha',
};

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const PLAN_IDS = {
  STANDARD: 'standard',
  PREMIUM: 'premium',
  PRO_ELITE: 'pro_elite',
  ALPHA: 'alpha',
};

export const PLANS = {
  [PLAN_IDS.STANDARD]: {
    id: PLAN_IDS.STANDARD,
    name: 'Standard',
    price: 300,
    color: COLORS.purple,
    badge: null,
    sections: [
      {
        title: 'Control Center',
        color: COLORS.purple,
        items: [
          'Dashboard',
          'Customer Transaction',
          'Ticket Out',
          'Bonus',
          'Notes',
          'Raffles',
        ],
      },
      {
        title: 'Readings',
        color: COLORS.purple,
        items: [
          'Employee Sessions',
          'Reading Sessions',
          'Record Reading Sessions',
        ],
      },
    ],
  },
  [PLAN_IDS.PREMIUM]: {
    id: PLAN_IDS.PREMIUM,
    name: 'Premium',
    price: 450,
    color: COLORS.blue,
    badge: 'POPULAR',
    sections: [
      {
        title: 'Control Center',
        color: COLORS.blue,
        note: 'All Standard features',
      },
      {
        title: 'Readings',
        color: COLORS.blue,
        items: [
          'Employee Sessions',
          'Reading Sessions',
          'Record Reading Sessions',
          'Point Watching',
        ],
      },
      {
        title: 'Accounting',
        color: COLORS.blue,
        items: [
          'Weekly Report',
          'Black List',
          'Match Privilege',
          'Inactive/Active List',
        ],
      },
    ],
  },
  [PLAN_IDS.PRO_ELITE]: {
    id: PLAN_IDS.PRO_ELITE,
    name: 'Pro Elite',
    price: 650,
    color: COLORS.goldDark,
    badge: null,
    sections: [
      {
        title: 'Control Center',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        title: 'Readings',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        title: 'Accounting',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        title: 'Extras',
        color: COLORS.goldDark,
        items: [
          'NFC Tags for Ticketout',
          'Link Accounts',
          'Customer Loan Report',
          'Zelle Report',
          'Employee Short Report',
          'Employees Loan Report',
        ],
      },
    ],
  },
  [PLAN_IDS.ALPHA]: {
    id: PLAN_IDS.ALPHA,
    name: 'Alpha',
    price: 250,
    color: COLORS.gold,
    badge: null,
    sections: [
      {
        title: 'Control Center',
        color: COLORS.gold,
        note: 'All Features Included',
      },
      { title: 'Readings', color: COLORS.gold, note: 'All Features Included' },
      {
        title: 'Accounting',
        color: COLORS.gold,
        note: 'All Features Included',
      },
      { title: 'Extras', color: COLORS.gold, note: 'All Features Included' },
      {
        title: 'All Add-Ons',
        color: COLORS.gold,
        items: [
          'Customer Mobile',
          'Reading Machine Picture',
          'Security Dashboard',
          'Link Accounts',
        ],
      },
    ],
  },
};

export const GLOBAL_PLAN_ORDER = [
  PLAN_IDS.STANDARD,
  PLAN_IDS.PREMIUM,
  PLAN_IDS.PRO_ELITE,
];

// ---------------------------------------------------------------------------
// Add-ons (Global Company only)
// ---------------------------------------------------------------------------

export const ADD_ONS = [
  { id: 'mobile_match', name: 'Customer Mobile', price: 50 },
  { id: 'reading_pictures', name: 'Reading Machine Picture', price: 150 },
  { id: 'security_dashboard', name: 'Security Dashboard', price: 150 },
  { id: 'link_accounts', name: 'Link Accounts', price: 100 },
];

export const ADD_ON_BY_ID = ADD_ONS.reduce((acc, a) => {
  acc[a.id] = a;
  return acc;
}, {});

// ---------------------------------------------------------------------------
// License durations
// ---------------------------------------------------------------------------

export const DURATIONS = [
  { id: '1', label: '1 Month', months: 1, discount: 0, badge: null },
  {
    id: '3',
    label: '3 Months',
    months: 3,
    discount: 0.05,
    badge: 'Save 5%',
    badgeColor: '#5b6b8a',
  },
  {
    id: '6',
    label: '6 Months',
    months: 6,
    discount: 0.1,
    badge: 'Save 10%',
    badgeColor: COLORS.green,
  },
  {
    id: '12',
    label: '1 Year',
    months: 12,
    discount: 0.15,
    badge: 'Best Value',
    badgeColor: COLORS.gold,
  },
];

export const DURATION_BY_ID = DURATIONS.reduce((acc, d) => {
  acc[d.id] = d;
  return acc;
}, {});

// ---------------------------------------------------------------------------
// Payment methods
// ---------------------------------------------------------------------------

export const PAYMENT_METHODS = {
  CASH_ZELLE: 'cash_zelle',
  CARD: 'card',
  BANK: 'bank',
  PENDING: 'pending',
};

export const PAYMENT_METHOD_LIST = [
  {
    id: PAYMENT_METHODS.CASH_ZELLE,
    label: 'Cash / Zelle',
    color: COLORS.gold,
    badge: 'MOST COMMON',
  },
  {
    id: PAYMENT_METHODS.PENDING,
    label: 'Collect Later',
    color: COLORS.purple,
    badge: 'PENDING',
  },
];

// ---------------------------------------------------------------------------
// Plan -> backend role bundle
// ---------------------------------------------------------------------------

// Settings access is mandatory for every package — without these the
// company has no way to manage rules, machines, games, bonuses, or
// employees. They are intentionally NOT optional add-ons.
const BASE_SETTINGS_ROLES = [
  'ROLE_SETTINGS',
  'ROLE_RULES',
  'ROLE_MACHINE',
  'ROLE_GAME',
  'ROLE_ASSIGN_MACHINE',
  'ROLE_MANAGE_BONUS',
  'ROLE_MANAGE_USERS',
];

const STANDARD_ROLES = [
  ...BASE_SETTINGS_ROLES,
  'ROLE_DASHBOARD',
  'ROLE_TRANSACTION',
  'ROLE_CUSTOMER',
  'ROLE_TICKET_OUT',
  'ROLE_BONUS',
  'ROLE_RAFFLE',
  // Raffle mobile access is a companion to Raffle (not a paid add-on), so
  // it's available on every plan that includes raffles.
  'ROLE_RAFFLE_MOBILE',
  'ROLE_NOTE',
  'ROLE_CHECK_IN_OUT',
  'ROLE_EMPLOYEE_SESSION',
  'ROLE_READING',
  'ROLE_MASTER_READING',
  'ROLE_ADD_MACHINE_READING',
  'ROLE_OPEN_LAST_TWO_READINGS',
];

const PREMIUM_ROLES = [
  ...STANDARD_ROLES,
  'ROLE_POINT_WATCHING',
  'ROLE_ACCOUNTING',
  'ROLE_REPORT',
];

// Pro Elite includes link-accounts and machine-picture by default, but
// security dashboard remains a paid add-on (its roles are intentionally
// excluded here so it shows up in the add-on picker for Pro Elite).
// ATM Transaction is intentionally NOT here — it is an Alpha-only
// feature that gates the Manager Summary chip & Manager Settings in
// the Report view.
const PRO_ELITE_ROLES = [
  ...PREMIUM_ROLES,
  'ROLE_TICKET_NFC',
  'ROLE_CUSTOMER_LOAN_REPORT',
  'ROLE_EMPLOYEE_SHORT_REPORT',
  'ROLE_ZELLE_REPORT',
  'ROLE_MACHINE_PICTURE',
  'ROLE_OPEN_ALL_WEEKLY_REPORT',
  'ROLE_LINK_ACCOUNTS',
];

const ALPHA_ROLES = [
  ...PRO_ELITE_ROLES,
  'ROLE_SECURITY',
  'ROLE_CUSTOMER_WALLET_PASS',
  'ROLE_CUSTOMER_PRIVILEGIE',
  'ROLE_CUSTOMER_MOBILE',
  'ROLE_ATM_TRANSACTION',
];

export const PLAN_ROLES = {
  [PLAN_IDS.STANDARD]: STANDARD_ROLES,
  [PLAN_IDS.PREMIUM]: PREMIUM_ROLES,
  [PLAN_IDS.PRO_ELITE]: PRO_ELITE_ROLES,
  [PLAN_IDS.ALPHA]: ALPHA_ROLES,
};

// Per-add-on extra roles (granted on top of the plan)
export const ADD_ON_ROLES = {
  mobile_match: ['ROLE_CUSTOMER_MOBILE'],
  reading_pictures: ['ROLE_MACHINE_PICTURE'],
  security_dashboard: ['ROLE_SECURITY', 'ROLE_CUSTOMER_WALLET_PASS'],
  link_accounts: ['ROLE_LINK_ACCOUNTS'],
};

// True when every role granted by the add-on is already in the plan's
// role bundle. Such add-ons should be hidden from the picker because
// the plan already covers them.
export function isAddOnIncludedInPlan(addOnId, planId) {
  const planRoles = new Set(PLAN_ROLES[planId] || []);
  const addOnRoles = ADD_ON_ROLES[addOnId] || [];
  if (addOnRoles.length === 0) return false;
  return addOnRoles.every((r) => planRoles.has(r));
}

// Add-ons available for purchase under the given plan: the configured
// add-ons minus any whose features the plan already includes.
export function getAvailableAddOns(planId) {
  return ADD_ONS.filter((a) => !isAddOnIncludedInPlan(a.id, planId));
}

// "Link accounts" is active for a company when the plan already includes
// it (Pro Elite, Alpha) or when the add-on is selected.
export function hasLinkAccounts(planId, addOnIds) {
  if (isAddOnIncludedInPlan('link_accounts', planId)) return true;
  return Array.isArray(addOnIds) && addOnIds.includes('link_accounts');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function generateLicenseKeyHex() {
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    out += Math.floor(Math.random() * 16)
      .toString(16)
      .toUpperCase();
  }
  return out;
}

export function generateCompanyId() {
  const n = Math.floor(Math.random() * 50) + 1;
  return String(n).padStart(3, '0');
}

// Trial date logic per spec section 4.3.
// - Find the next 15th of the month.
// - If less than 25 days away, skip to the following month's 15th.
// - Trial days = difference in days between now and that 15th.
// - License expiry = trial end date + selected duration months.
export function computeTrialDates(now, durationMonths) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  let target = new Date(today.getFullYear(), today.getMonth(), 15);
  if (target <= today) {
    target = new Date(today.getFullYear(), today.getMonth() + 1, 15);
  }
  const msPerDay = 24 * 60 * 60 * 1000;
  let diffDays = Math.round((target - today) / msPerDay);
  if (diffDays < 25) {
    target = new Date(target.getFullYear(), target.getMonth() + 1, 15);
    diffDays = Math.round((target - today) / msPerDay);
  }

  const expiry = new Date(target);
  expiry.setMonth(expiry.getMonth() + Number(durationMonths || 1));

  return {
    startDate: today,
    trialEnd: target,
    trialDays: diffDays,
    expiryDate: expiry,
  };
}

export function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function computeAuthorities(planId, addOnIds) {
  const set = new Set(PLAN_ROLES[planId] || []);
  (addOnIds || []).forEach((id) => {
    (ADD_ON_ROLES[id] || []).forEach((r) => set.add(r));
  });
  return Array.from(set);
}

// Best-effort match: a stored `planName` string -> wizard plan id.
// Mirrors the inference used by CompanyWizardV3 for legacy rows that
// pre-date the explicit planId field.
export function inferPlanIdFromName(planName) {
  if (!planName) return PLAN_IDS.PREMIUM;
  const lower = String(planName).toLowerCase();
  if (lower.includes('alpha')) return PLAN_IDS.ALPHA;
  if (lower.includes('elite') || lower.includes('pro'))
    return PLAN_IDS.PRO_ELITE;
  if (lower.includes('standard') || lower.includes('basic'))
    return PLAN_IDS.STANDARD;
  return PLAN_IDS.PREMIUM;
}

// Returns the role set a company is entitled to based on its stored
// authorities (if present) or, as a fallback, its planName + addOns.
// Used by the Employee Management wizard to constrain which permissions
// an admin can grant — you cannot grant a feature the company hasn't
// paid for.
//
// BASE_SETTINGS_ROLES are always unioned in to mirror the backend's
// mandatory-settings injection in CompanyService — without this,
// legacy companies that haven't yet been re-saved would appear to
// lack Settings access in the wizard.
export function getCompanyAllowedAuthorities(company) {
  if (!company) return new Set();
  const out = new Set(BASE_SETTINGS_ROLES);

  const rawAuths = company.authorities;
  if (Array.isArray(rawAuths) && rawAuths.length) {
    rawAuths.forEach((a) => {
      const name = typeof a === 'string' ? a : a && a.authority;
      if (name) out.add(name);
    });
  } else if (company.planName) {
    const planId = inferPlanIdFromName(company.planName);
    computeAuthorities(planId, company.addOns || []).forEach((r) => out.add(r));
  }

  // Raffle mobile access rides along with the Raffle feature — it isn't a
  // separately-sold role. Derive it here so existing companies (whose
  // stored authorities predate this role) can still grant it without
  // needing the company to be re-saved.
  if (out.has('ROLE_RAFFLE')) {
    out.add('ROLE_RAFFLE_MOBILE');
  }

  return out;
}

// Pricing rules per spec section 3.10.
export function computePricing({ planId, addOnIds, durationId }) {
  const plan = PLANS[planId];
  const duration = DURATION_BY_ID[durationId] || DURATIONS[0];
  if (!plan) {
    return {
      monthly: 0,
      gross: 0,
      discount: 0,
      total: 0,
      months: duration.months,
    };
  }
  const addOnTotal = (addOnIds || []).reduce((sum, id) => {
    const a = ADD_ON_BY_ID[id];
    return sum + (a ? a.price : 0);
  }, 0);
  const monthly = plan.price + addOnTotal;
  const gross = monthly * duration.months;
  const discount = Math.round(gross * duration.discount);
  const total = gross - discount;
  return {
    monthly,
    gross,
    discount,
    total,
    months: duration.months,
    discountPct: duration.discount,
  };
}
